import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { pricingService, roomsService } from '../services/api';
import { format, parseISO, addDays, startOfWeek, endOfWeek } from 'date-fns';
import { ClipboardList as ClipboardListIcon, Hourglass as HourglassIcon, Pencil as PencilIcon, Trash2 as Trash2Icon } from 'lucide-react';

const PriceManagement = ({ user }) => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [dynamicPrices, setDynamicPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'

  // Form state for price management
  const [priceForm, setPriceForm] = useState({
    startDate: '',
    endDate: '',
    price: '',
    name: '',
    description: ''
  });

  const [basePriceForm, setBasePriceForm] = useState({
    roomTypeId: '',
    basePrice: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load room types and their dynamic prices
      const [roomTypesResponse, pricingOverviewResponse] = await Promise.all([
        roomsService.getRoomTypes(),
        pricingService.getPricingOverview()
      ]);

      const roomTypesData = roomTypesResponse.data?.roomTypes || roomTypesResponse.roomTypes || [];
      const pricingData = pricingOverviewResponse.data || [];

      setRoomTypes(roomTypesData);

      // Organize dynamic prices by room type
      const pricesMap = {};
      pricingData.forEach(roomType => {
        pricesMap[roomType.id] = roomType.dynamicPrices || [];
      });
      setDynamicPrices(pricesMap);

    } catch (error) {
      console.error('Error loading pricing data:', error);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const generateDateRange = () => {
    const dates = [];
    const start = viewMode === 'week' 
      ? startOfWeek(currentWeek, { weekStartsOn: 1 }) // Start on Monday
      : new Date(currentWeek.getFullYear(), currentWeek.getMonth(), 1);
    
    const end = viewMode === 'week'
      ? endOfWeek(currentWeek, { weekStartsOn: 1 })
      : new Date(currentWeek.getFullYear(), currentWeek.getMonth() + 1, 0);

    let currentDate = new Date(start);
    while (currentDate <= end) {
      dates.push(new Date(currentDate));
      currentDate = addDays(currentDate, 1);
    }
    return dates;
  };

  const getPriceForDate = (roomTypeId, date) => {
    const roomTypePrices = dynamicPrices[roomTypeId] || [];
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Find dynamic price that covers this date
    const dynamicPrice = roomTypePrices.find(price => {
      const startDate = format(parseISO(price.startDate), 'yyyy-MM-dd');
      const endDate = format(parseISO(price.endDate), 'yyyy-MM-dd');
      return dateStr >= startDate && dateStr <= endDate && price.isActive;
    });

    if (dynamicPrice) {
      return {
        price: dynamicPrice.price,
        isDynamic: true,
        priceData: dynamicPrice
      };
    }

    // Fall back to base price
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    return {
      price: roomType?.basePrice || roomType?.defaultRate || 0,
      isDynamic: false,
      priceData: null
    };
  };

  const handleCreateDynamicPrice = async (e) => {
    e.preventDefault();
    
    if (!selectedRoomType || !priceForm.startDate || !priceForm.endDate || !priceForm.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate dates on frontend first
    const startDate = new Date(priceForm.startDate + 'T00:00:00');
    const endDate = new Date(priceForm.endDate + 'T23:59:59');
    
    if (startDate >= endDate) {
      toast.error('End date must be after start date');
      return;
    }

    try {
      const priceData = {
        roomTypeId: selectedRoomType.id,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        price: parseFloat(priceForm.price),
        name: priceForm.name || undefined,
        description: priceForm.description || undefined
      };

      console.log('Sending price data:', priceData);

      if (editingPrice) {
        await pricingService.updateDynamicPrice(editingPrice.id, priceData);
        toast.success('Dynamic price updated successfully');
      } else {
        await pricingService.createDynamicPrice(priceData);
        toast.success('Dynamic price created successfully');
      }

      setShowPriceModal(false);
      resetPriceForm();
      loadData();
    } catch (error) {
      console.error('Error saving dynamic price:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to save dynamic price');
    }
  };

  const handleUpdateBasePrice = async (e) => {
    e.preventDefault();
    
    if (!basePriceForm.roomTypeId || !basePriceForm.basePrice) {
      toast.error('Please select room type and enter base price');
      return;
    }

    try {
      await pricingService.updateBasePrice(basePriceForm.roomTypeId, parseFloat(basePriceForm.basePrice));
      toast.success('Base price updated successfully');
      setBasePriceForm({ roomTypeId: '', basePrice: '' });
      loadData();
    } catch (error) {
      console.error('Error updating base price:', error);
      toast.error('Failed to update base price');
    }
  };

  const handleDeleteDynamicPrice = async (priceId) => {
    if (!window.confirm('Are you sure you want to delete this dynamic price?')) {
      return;
    }

    try {
      await pricingService.deleteDynamicPrice(priceId);
      toast.success('Dynamic price deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting dynamic price:', error);
      toast.error('Failed to delete dynamic price');
    }
  };

  const openPriceModal = (roomType, existingPrice = null) => {
    setSelectedRoomType(roomType);
    setEditingPrice(existingPrice);
    
    if (existingPrice) {
      setPriceForm({
        startDate: format(parseISO(existingPrice.startDate), 'yyyy-MM-dd'),
        endDate: format(parseISO(existingPrice.endDate), 'yyyy-MM-dd'),
        price: existingPrice.price.toString(),
        name: existingPrice.name || '',
        description: existingPrice.description || ''
      });
    } else {
      resetPriceForm();
    }
    
    setShowPriceModal(true);
  };

  const resetPriceForm = () => {
    setPriceForm({
      startDate: '',
      endDate: '',
      price: '',
      name: '',
      description: ''
    });
    setEditingPrice(null);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeek);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentWeek(newDate);
  };

  const dateRange = generateDateRange();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
          <div>Loading pricing data...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: '#F5F3EB',
        borderRadius: '8px',
        border: '1px solid #E3DFD3'
      }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600', color: '#1A3E2A' }}>
            Dynamic Pricing Management
          </h1>
          <p style={{ margin: 0, color: '#5F6B61', fontSize: '14px' }}>
            Manage base prices and dynamic pricing for different date ranges
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="week">Week View</option>
            <option value="month">Month View</option>
          </select>
        </div>
      </div>

      {/* Navigation Controls */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #E3DFD3'
      }}>
        <button
          onClick={() => navigateWeek(-1)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1A3E2A',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← Previous {viewMode}
        </button>
        
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
          {viewMode === 'week' 
            ? `Week of ${format(dateRange[0], 'MMM dd')} - ${format(dateRange[dateRange.length - 1], 'MMM dd, yyyy')}`
            : format(currentWeek, 'MMMM yyyy')
          }
        </h2>
        
        <button
          onClick={() => navigateWeek(1)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1A3E2A',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Next {viewMode} →
        </button>
      </div>

      {/* Base Price Management */}
      <div style={{
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #E3DFD3'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
          Update Base Prices
        </h3>
        <form onSubmit={handleUpdateBasePrice} style={{ display: 'flex', gap: '12px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              Room Type
            </label>
            <select
              value={basePriceForm.roomTypeId}
              onChange={(e) => setBasePriceForm({ ...basePriceForm, roomTypeId: e.target.value })}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '200px'
              }}
              required
            >
              <option value="">Select Room Type</option>
              {roomTypes.map(roomType => (
                <option key={roomType.id} value={roomType.id}>
                  {roomType.name} (Current: ₹{roomType.basePrice || roomType.defaultRate})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              New Base Price
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={basePriceForm.basePrice}
              onChange={(e) => setBasePriceForm({ ...basePriceForm, basePrice: e.target.value })}
              placeholder="Enter price"
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                width: '120px'
              }}
              required
            />
          </div>
          <button
            type="submit"
            style={{
              padding: '8px 16px',
              backgroundColor: '#1A3E2A',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Update Base Price
          </button>
        </form>
      </div>

      {/* Pricing Grid */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #E3DFD3',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ backgroundColor: '#F5F3EB' }}>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  fontSize: '14px',
                  borderBottom: '2px solid #E3DFD3',
                  minWidth: '200px',
                  position: 'sticky',
                  left: 0,
                  backgroundColor: '#F5F3EB',
                  zIndex: 10
                }}>
                  Room Type
                </th>
                {dateRange.map(date => (
                  <th key={date.toISOString()} style={{
                    padding: '12px 8px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '12px',
                    borderBottom: '2px solid #E3DFD3',
                    minWidth: '80px'
                  }}>
                    <div>{format(date, 'EEE')}</div>
                    <div style={{ fontSize: '11px', color: '#5F6B61' }}>
                      {format(date, 'MMM dd')}
                    </div>
                  </th>
                ))}
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '14px',
                  borderBottom: '2px solid #E3DFD3',
                  minWidth: '120px'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {roomTypes.map(roomType => (
                <tr key={roomType.id} style={{ borderBottom: '1px solid #EEEBE1' }}>
                  <td style={{
                    padding: '16px',
                    fontWeight: '500',
                    fontSize: '14px',
                    borderRight: '1px solid #EEEBE1',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'white',
                    zIndex: 5
                  }}>
                    <div>{roomType.name}</div>
                    <div style={{ fontSize: '12px', color: '#5F6B61' }}>
                      Base: ₹{roomType.basePrice || roomType.defaultRate}
                    </div>
                  </td>
                  {dateRange.map(date => {
                    const priceInfo = getPriceForDate(roomType.id, date);
                    return (
                      <td key={date.toISOString()} style={{
                        padding: '8px',
                        textAlign: 'center',
                        fontSize: '13px',
                        backgroundColor: priceInfo.isDynamic ? '#F3E6CB' : 'white',
                        borderRight: '1px solid #F5F3EB',
                        cursor: priceInfo.isDynamic ? 'pointer' : 'default'
                      }}
                      onClick={() => priceInfo.isDynamic && openPriceModal(roomType, priceInfo.priceData)}
                      title={priceInfo.isDynamic ? `Dynamic Price: ${priceInfo.priceData.name || 'Custom Rate'}` : 'Base Price'}
                      >
                        <div style={{ 
                          fontWeight: priceInfo.isDynamic ? '600' : '400',
                          color: priceInfo.isDynamic ? '#9E712E' : '#36433B'
                        }}>
                          ₹{priceInfo.price}
                        </div>
                        {priceInfo.isDynamic && (
                          <div style={{ fontSize: '10px', color: '#684A1F' }}>
                            Dynamic
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => openPriceModal(roomType)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#1A3E2A',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginBottom: '4px',
                        display: 'block',
                        width: '100%'
                      }}
                    >
                      Add Price
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active Dynamic Prices List */}
      <div style={{
        marginTop: '24px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #E3DFD3'
      }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #E3DFD3' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            Active Dynamic Prices
          </h3>
        </div>
        <div style={{ padding: '20px' }}>
          {Object.keys(dynamicPrices).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#5F6B61' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}><ClipboardListIcon size="1em" aria-hidden="true" /></div>
              <p>No dynamic prices configured yet.</p>
              <p style={{ fontSize: '14px' }}>Create your first dynamic price using the grid above.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {Object.entries(dynamicPrices).map(([roomTypeId, prices]) => {
                const roomType = roomTypes.find(rt => rt.id === roomTypeId);
                const activePrices = prices.filter(p => p.isActive);
                
                if (activePrices.length === 0) return null;
                
                return (
                  <div key={roomTypeId} style={{
                    border: '1px solid #E3DFD3',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#F5F3EB'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
                      {roomType?.name}
                    </h4>
                    <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '12px' }}>
                      Base Price: ₹{roomType?.basePrice || roomType?.defaultRate}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {activePrices.map(price => (
                        <div key={price.id} style={{
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #E3DFD3'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px', color: '#9E712E' }}>
                                ₹{price.price}
                              </div>
                              {price.name && (
                                <div style={{ fontSize: '12px', fontWeight: '500', color: '#36433B' }}>
                                  {price.name}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => openPriceModal(roomType, price)}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#B68235',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                                title="Edit"
                              >
                                <PencilIcon size="1em" aria-hidden="true" />
                              </button>
                              <button
                                onClick={() => handleDeleteDynamicPrice(price.id)}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#A13F2F',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                                title="Delete"
                              >
                                <Trash2Icon size="1em" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                          <div style={{ fontSize: '12px', color: '#5F6B61' }}>
                            {format(parseISO(price.startDate), 'MMM dd, yyyy')} - {format(parseISO(price.endDate), 'MMM dd, yyyy')}
                          </div>
                          {price.description && (
                            <div style={{ fontSize: '11px', color: '#A5A89B', marginTop: '4px' }}>
                              {price.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Dynamic Price Modal */}
      {showPriceModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#E9DFCE',
            borderRadius: '8px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E3DFD3'
            }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {editingPrice ? 'Edit Dynamic Price' : 'Add Dynamic Price'}
              </h3>
              <p style={{ margin: '8px 0 0 0', color: '#5F6B61', fontSize: '14px' }}>
                {selectedRoomType?.name}
              </p>
            </div>
            
            <form onSubmit={handleCreateDynamicPrice} style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  Price Name (Optional)
                </label>
                <input
                  type="text"
                  value={priceForm.name}
                  onChange={(e) => setPriceForm({ ...priceForm, name: e.target.value })}
                  placeholder="e.g., Summer Season, Festival Week"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={priceForm.startDate}
                    onChange={(e) => setPriceForm({ ...priceForm, startDate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={priceForm.endDate}
                    onChange={(e) => setPriceForm({ ...priceForm, endDate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #D2CDBF',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  Price per Night *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceForm.price}
                  onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })}
                  placeholder="Enter price"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  required
                />
                <div style={{ marginTop: '4px', fontSize: '12px', color: '#5F6B61' }}>
                  Base price: ₹{selectedRoomType?.basePrice || selectedRoomType?.defaultRate}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                  Description (Optional)
                </label>
                <textarea
                  value={priceForm.description}
                  onChange={(e) => setPriceForm({ ...priceForm, description: e.target.value })}
                  placeholder="Additional details about this pricing period"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D2CDBF',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowPriceModal(false);
                    resetPriceForm();
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#5F6B61',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#1A3E2A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {editingPrice ? 'Update Price' : 'Create Price'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceManagement;